import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  CircularProgress,
  TablePagination,
  Stack,
  Button,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FileDownload as ExcelIcon,
  Print as PrintIcon,
  Clear as ClearIcon,
  MonitorHeart as MonitorIcon,
  AddCircle as CreatedIcon,
  CheckCircle as ApprovedIcon,
  PublishedWithChanges as PostedIcon,
  Assessment as TotalIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import { useTheme } from '@mui/material/styles';
import apiClient from '../../services/api';
import ExcelJS from 'exceljs';
import config from '../../config';

// ── Types ──────────────────────────────────────────────────────────
interface OperationRecord {
  documentType: string;
  documentNumber: string;
  action: string;
  actionDate: string;
  actionByUserName: string;
  status: string;
  branchName: string;
  branchNameAr?: string;
  amount: number;
  journalEntryId?: number;
  journalEntryNumber?: string;
  description?: string;
  descriptionAr?: string;
}

// ── Colour maps ────────────────────────────────────────────────────
const docTypeColors: Record<string, string> = {
  JournalEntry: '#667eea',
  PurchaseInvoice: '#2196f3',
  PurchaseReturn: '#ff9800',
  BranchTransfer: '#1976d2',
  WarehouseTransfer: '#9c27b0',
  StockIssue: '#e65100',
  StockCount: '#00695c',
  BranchIssue: '#e65100',
  BranchIssueRequest: '#7b1fa2',
  Payroll: '#2e7d32',
  Voucher: '#f44336',
  DailySale: '#ff6f00',
  CustodySettlement: '#795548',
};

const docTypeLabelsAr: Record<string, string> = {
  JournalEntry: '\u0642\u064a\u062f \u064a\u0648\u0645\u064a\u0629',
  PurchaseInvoice: '\u0641\u0627\u062a\u0648\u0631\u0629 \u0645\u0634\u062a\u0631\u064a\u0627\u062a',
  PurchaseReturn: '\u0645\u0631\u062a\u062c\u0639 \u0645\u0634\u062a\u0631\u064a\u0627\u062a',
  BranchTransfer: '\u062a\u062d\u0648\u064a\u0644 \u0641\u0631\u0648\u0639',
  WarehouseTransfer: '\u062a\u062d\u0648\u064a\u0644 \u0645\u062e\u0627\u0632\u0646',
  StockIssue: '\u0635\u0631\u0641 \u0645\u062e\u0632\u0648\u0646',
  StockCount: '\u062c\u0631\u062f',
  BranchIssue: '\u0635\u0631\u0641 \u0641\u0631\u0648\u0639',
  BranchIssueRequest: '\u0637\u0644\u0628 \u0635\u0631\u0641',
  Payroll: '\u0631\u0648\u0627\u062a\u0628',
  Voucher: '\u0633\u0646\u062f',
  DailySale: '\u0645\u0628\u064a\u0639\u0627\u062a \u064a\u0648\u0645\u064a\u0629',
  CustodySettlement: '\u062a\u0633\u0648\u064a\u0629 \u0639\u0647\u062f\u0629',
};

const actionLabelsAr: Record<string, string> = {
  Created: '\u0625\u0646\u0634\u0627\u0621',
  Modified: '\u062a\u0639\u062f\u064a\u0644',
  Approved: '\u0627\u0639\u062a\u0645\u0627\u062f',
  Received: '\u0627\u0633\u062a\u0644\u0627\u0645',
  Posted: '\u062a\u0631\u062d\u064a\u0644',
};

const actionChipColor: Record<string, 'info' | 'warning' | 'success' | 'primary' | 'default'> = {
  Created: 'info',
  Modified: 'warning',
  Approved: 'success',
  Received: 'primary',
  Posted: 'success',
};

const statusChipColor: Record<string, 'warning' | 'success' | 'primary' | 'default'> = {
  Draft: 'warning',
  Approved: 'success',
  Received: 'primary',
  Posted: 'success',
};

const statusLabelsAr: Record<string, string> = {
  Draft: '\u0645\u0633\u0648\u062f\u0629',
  Approved: '\u0645\u0639\u062a\u0645\u062f',
  Received: '\u0645\u0633\u062a\u0644\u0645',
  Posted: '\u0645\u0631\u062d\u0644',
};

const DOC_TYPES = [
  'All', 'JournalEntry', 'PurchaseInvoice', 'PurchaseReturn', 'BranchTransfer',
  'WarehouseTransfer', 'StockIssue', 'InternalIssue', 'StockCount', 'BranchIssue', 'BranchIssueRequest',
  'Payroll', 'Voucher', 'DailySale', 'CustodySettlement',
];

const ACTIONS = ['All', 'Created', 'Modified', 'Approved', 'Received', 'Posted'];
const STATUSES = ['All', 'Draft', 'Approved', 'Received', 'Posted'];

// ── Component ──────────────────────────────────────────────────────
const OperationsMonitorPage: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { minDate, maxDate } = useFiscalDateRange();

  // ── Translations ───────────────────────────────────────────────
  const t = useMemo(() => ({
    title: isRTL ? '\u0645\u0631\u0627\u0642\u0628\u0629 \u0627\u0644\u0639\u0645\u0644\u064a\u0627\u062a' : 'Operations Monitor',
    totalOperations: isRTL ? '\u0625\u062c\u0645\u0627\u0644\u064a \u0627\u0644\u0639\u0645\u0644\u064a\u0627\u062a' : 'Total Operations',
    created: isRTL ? '\u0625\u0646\u0634\u0627\u0621' : 'Created',
    posted: isRTL ? '\u062a\u0631\u062d\u064a\u0644' : 'Posted',
    approved: isRTL ? '\u0627\u0639\u062a\u0645\u0627\u062f' : 'Approved',
    fromDate: isRTL ? '\u0645\u0646 \u062a\u0627\u0631\u064a\u062e' : 'From Date',
    toDate: isRTL ? '\u0625\u0644\u0649 \u062a\u0627\u0631\u064a\u062e' : 'To Date',
    documentType: isRTL ? '\u0646\u0648\u0639 \u0627\u0644\u0645\u0633\u062a\u0646\u062f' : 'Document Type',
    action: isRTL ? '\u0627\u0644\u0625\u062c\u0631\u0627\u0621' : 'Action',
    status: isRTL ? '\u0627\u0644\u062d\u0627\u0644\u0629' : 'Status',
    search: isRTL ? '\u0628\u062d\u062b' : 'Search',
    searchPlaceholder: isRTL ? '\u0631\u0642\u0645 \u0627\u0644\u0645\u0633\u062a\u0646\u062f\u060c \u0627\u0644\u0645\u0633\u062a\u062e\u062f\u0645\u060c \u0627\u0644\u0648\u0635\u0641' : 'Doc number, user, description',
    clearFilters: isRTL ? '\u0645\u0633\u062d \u0627\u0644\u0641\u0644\u0627\u062a\u0631' : 'Clear Filters',
    all: isRTL ? '\u0627\u0644\u0643\u0644' : 'All',
    documentNumber: isRTL ? '\u0631\u0642\u0645 \u0627\u0644\u0645\u0633\u062a\u0646\u062f' : 'Document Number',
    date: isRTL ? '\u0627\u0644\u062a\u0627\u0631\u064a\u062e' : 'Date',
    user: isRTL ? '\u0627\u0644\u0645\u0633\u062a\u062e\u062f\u0645' : 'User',
    branch: isRTL ? '\u0627\u0644\u0641\u0631\u0639' : 'Branch',
    amount: isRTL ? '\u0627\u0644\u0645\u0628\u0644\u063a' : 'Amount',
    journalEntry: isRTL ? '\u0642\u064a\u062f \u064a\u0648\u0645\u064a\u0629' : 'Journal Entry',
    noData: isRTL ? '\u0644\u0627 \u062a\u0648\u062c\u062f \u0628\u064a\u0627\u0646\u0627\u062a' : 'No data found',
    loading: isRTL ? '\u062c\u0627\u0631\u064a \u0627\u0644\u062a\u062d\u0645\u064a\u0644...' : 'Loading...',
    refresh: isRTL ? '\u062a\u062d\u062f\u064a\u062b' : 'Refresh',
    print: isRTL ? '\u0637\u0628\u0627\u0639\u0629' : 'Print',
    exportExcel: isRTL ? '\u062a\u0635\u062f\u064a\u0631 Excel' : 'Export Excel',
    rowsPerPage: isRTL ? '\u0639\u062f\u062f \u0627\u0644\u0635\u0641\u0648\u0641:' : 'Rows per page:',
  }), [isRTL]);

  // ── State ──────────────────────────────────────────────────────
  const [operations, setOperations] = useState<OperationRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  // Filters
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterDocType, setFilterDocType] = useState('All');
  const [filterAction, setFilterAction] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // ── Effects ────────────────────────────────────────────────────
  useEffect(() => {
    loadOperations();
    loadCompanyInfo();
  }, []);

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (err) {
      console.error('Failed to load company info:', err);
    }
  };

  const loadOperations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterDateFrom) params.append('fromDate', filterDateFrom);
      if (filterDateTo) params.append('toDate', filterDateTo);
      if (filterDocType && filterDocType !== 'All') params.append('documentType', filterDocType);
      if (filterAction && filterAction !== 'All') params.append('action', filterAction);
      if (filterStatus && filterStatus !== 'All') params.append('status', filterStatus);
      const response = await apiClient.get(`/OperationsMonitor?${params.toString()}`);
      setOperations(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to load operations:', err);
      setOperations([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Derived / filtered data ────────────────────────────────────
  const filteredOperations = useMemo(() => {
    if (!searchTerm) return operations;
    const s = searchTerm.toLowerCase();
    return operations.filter(op =>
      (op.documentNumber && op.documentNumber.toLowerCase().includes(s)) ||
      (op.actionByUserName && op.actionByUserName.toLowerCase().includes(s)) ||
      (op.description && op.description.toLowerCase().includes(s)) ||
      (op.descriptionAr && op.descriptionAr.toLowerCase().includes(s))
    );
  }, [operations, searchTerm]);

  const stats = useMemo(() => ({
    total: filteredOperations.length,
    created: filteredOperations.filter(o => o.action === 'Created').length,
    posted: filteredOperations.filter(o => o.action === 'Posted').length,
    approved: filteredOperations.filter(o => o.action === 'Approved').length,
  }), [filteredOperations]);

  const paginatedData = filteredOperations.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // ── Helpers ────────────────────────────────────────────────────
  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');

  const formatAmount = (val: number) =>
    new Intl.NumberFormat('en-GB', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(val);

  const toDateStr = (date: Date | null): string => {
    if (!date) return '';
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const getDocTypeLabel = (dt: string) => {
    if (dt === 'All') return t.all;
    return isRTL ? (docTypeLabelsAr[dt] || dt) : dt.replace(/([A-Z])/g, ' $1').trim();
  };

  const getActionLabel = (a: string) => {
    if (a === 'All') return t.all;
    return isRTL ? (actionLabelsAr[a] || a) : a;
  };

  const getStatusLabel = (s: string) => {
    if (s === 'All') return t.all;
    return isRTL ? (statusLabelsAr[s] || s) : s;
  };

  const clearFilters = () => {
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterDocType('All');
    setFilterAction('All');
    setFilterStatus('All');
    setSearchTerm('');
  };

  // ── Print ──────────────────────────────────────────────────────
  const handlePrint = () => {
    if (filteredOperations.length === 0) return;

    const logoHtml = companyInfo?.logoBase64
      ? `<img src="${companyInfo.logoBase64}" style="max-height:50px;display:block;margin:0 auto 8px;" />`
      : '';

    let rows = '';
    filteredOperations.forEach((op, i) => {
      rows += `<tr class="${i % 2 === 0 ? '' : 'odd'}">
        <td><span class="chip" style="background:${docTypeColors[op.documentType] || '#888'};color:#fff;">${getDocTypeLabel(op.documentType)}</span></td>
        <td style="font-weight:bold;color:#1976d2;">${op.documentNumber}</td>
        <td><span class="chip action-${op.action.toLowerCase()}">${getActionLabel(op.action)}</span></td>
        <td>${formatDate(op.actionDate)}</td>
        <td>${op.actionByUserName || ''}</td>
        <td><span class="chip status-${(op.status || '').toLowerCase()}">${getStatusLabel(op.status)}</span></td>
        <td>${isRTL ? (op.branchNameAr || op.branchName || '') : (op.branchName || '')}</td>
        <td style="text-align:right;font-family:monospace;">${formatAmount(op.amount)}</td>
        <td>${op.journalEntryNumber || '-'}</td>
      </tr>`;
    });

    const html = `<!DOCTYPE html><html dir="${isRTL ? 'rtl' : 'ltr'}">
<head><title>${t.title}</title>
<style>
  body { font-family: Arial, sans-serif; margin: 20px; font-size: 11px; color: #000; }
  .header { text-align: center; margin-bottom: 15px; }
  .header h1 { margin: 3px 0; font-size: 16px; }
  .header h2 { margin: 3px 0; font-size: 14px; color: #667eea; }
  table { width: 100%; border-collapse: collapse; margin-top: 10px; }
  th, td { border: 1px solid #ddd; padding: 5px 7px; text-align: ${isRTL ? 'right' : 'left'}; font-size: 10px; }
  th { background: linear-gradient(135deg, #667eea, #764ba2); color: #fff; font-size: 10px; }
  .odd { background: #fafafa; }
  .chip { display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 9px; font-weight: bold; }
  .action-created { background: #e3f2fd; color: #1565c0; }
  .action-modified { background: #fff3e0; color: #e65100; }
  .action-approved { background: #e8f5e9; color: #2e7d32; }
  .action-received { background: #e3f2fd; color: #1976d2; }
  .action-posted { background: #e8f5e9; color: #1b5e20; }
  .status-draft { background: #fff3e0; color: #e65100; }
  .status-approved { background: #e8f5e9; color: #2e7d32; }
  .status-received { background: #e3f2fd; color: #1976d2; }
  .status-posted { background: #e8f5e9; color: #1b5e20; }
  .stats { display: flex; gap: 15px; justify-content: center; margin-bottom: 15px; }
  .stat-box { padding: 8px 18px; border-radius: 8px; text-align: center; color: #fff; }
  @media print { body { margin: 10px; } @page { margin: 10mm; size: A4 landscape; } }
</style></head>
<body>
  <div class="header">
    ${logoHtml}
    <h1>${isRTL ? (companyInfo?.companyNameAr || companyInfo?.companyName || '') : (companyInfo?.companyName || '')}</h1>
    <h2>${t.title}</h2>
    <p style="color:#555;font-size:10px;">${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</p>
  </div>
  <div class="stats">
    <div class="stat-box" style="background:#667eea;">${t.totalOperations}: ${stats.total}</div>
    <div class="stat-box" style="background:#2196f3;">${t.created}: ${stats.created}</div>
    <div class="stat-box" style="background:#4caf50;">${t.posted}: ${stats.posted}</div>
    <div class="stat-box" style="background:#ff9800;">${t.approved}: ${stats.approved}</div>
  </div>
  <table>
    <thead><tr>
      <th>${t.documentType}</th>
      <th>${t.documentNumber}</th>
      <th>${t.action}</th>
      <th>${t.date}</th>
      <th>${t.user}</th>
      <th>${t.status}</th>
      <th>${t.branch}</th>
      <th style="text-align:right;">${t.amount}</th>
      <th>${t.journalEntry}</th>
    </tr></thead>
    <tbody>${rows}</tbody>
  </table>
</body></html>`;

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
      setTimeout(() => win.print(), 500);
    }
  };

  // ── Excel export ───────────────────────────────────────────────
  const exportToExcel = async () => {
    if (filteredOperations.length === 0) return;

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet(isRTL ? '\u0645\u0631\u0627\u0642\u0628\u0629 \u0627\u0644\u0639\u0645\u0644\u064a\u0627\u062a' : 'Operations Monitor');

    // Company logo
    if (companyInfo?.logoBase64) {
      try {
        const img = wb.addImage({ base64: companyInfo.logoBase64, extension: 'png' });
        ws.addImage(img, { tl: { col: 0, row: 0 }, ext: { width: 60, height: 60 } });
      } catch (e) { /* ignore */ }
    }

    // Company name
    ws.mergeCells('B1:I1');
    ws.getCell('B1').value = isRTL ? companyInfo?.companyNameAr || companyInfo?.companyName : companyInfo?.companyName || '';
    ws.getCell('B1').font = { size: 14, bold: true };
    ws.getCell('B1').alignment = { horizontal: 'center' };

    // Report title
    ws.mergeCells('B2:I2');
    ws.getCell('B2').value = t.title;
    ws.getCell('B2').font = { size: 12, bold: true, color: { argb: 'FF667EEA' } };
    ws.getCell('B2').alignment = { horizontal: 'center' };

    // Generated date
    ws.mergeCells('B3:I3');
    ws.getCell('B3').value = `${isRTL ? '\u062a\u0627\u0631\u064a\u062e \u0627\u0644\u062a\u0642\u0631\u064a\u0631' : 'Generated'}: ${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}`;
    ws.getCell('B3').alignment = { horizontal: 'center' };
    ws.addRow([]);

    // Header row
    const headers = isRTL
      ? ['\u0646\u0648\u0639 \u0627\u0644\u0645\u0633\u062a\u0646\u062f', '\u0631\u0642\u0645 \u0627\u0644\u0645\u0633\u062a\u0646\u062f', '\u0627\u0644\u0625\u062c\u0631\u0627\u0621', '\u0627\u0644\u062a\u0627\u0631\u064a\u062e', '\u0627\u0644\u0645\u0633\u062a\u062e\u062f\u0645', '\u0627\u0644\u062d\u0627\u0644\u0629', '\u0627\u0644\u0641\u0631\u0639', '\u0627\u0644\u0645\u0628\u0644\u063a', '\u0642\u064a\u062f \u064a\u0648\u0645\u064a\u0629']
      : ['Document Type', 'Document Number', 'Action', 'Date', 'User', 'Status', 'Branch', 'Amount', 'Journal Entry'];

    const hRow = ws.addRow(headers);
    hRow.eachCell(c => {
      c.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF667EEA' } };
      c.alignment = { horizontal: 'center', vertical: 'middle' };
      c.border = {
        top: { style: 'thin' }, bottom: { style: 'thin' },
        left: { style: 'thin' }, right: { style: 'thin' },
      };
    });
    hRow.height = 20;

    // Data rows
    filteredOperations.forEach((op, idx) => {
      const r = ws.addRow([
        getDocTypeLabel(op.documentType),
        op.documentNumber,
        getActionLabel(op.action),
        formatDate(op.actionDate),
        op.actionByUserName || '',
        getStatusLabel(op.status),
        isRTL ? (op.branchNameAr || op.branchName || '') : (op.branchName || ''),
        op.amount,
        op.journalEntryNumber || '-',
      ]);
      r.eachCell((c, colIdx) => {
        c.border = {
          top: { style: 'thin' }, bottom: { style: 'thin' },
          left: { style: 'thin' }, right: { style: 'thin' },
        };
        if (colIdx === 8) {
          c.numFmt = '#,##0.000';
          c.alignment = { horizontal: 'right' };
        }
      });
      if (idx % 2 === 0) {
        r.eachCell(c => {
          c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5FF' } };
        });
      }
    });

    // Column widths
    ws.columns = [
      { width: 20 }, { width: 20 }, { width: 14 }, { width: 14 },
      { width: 20 }, { width: 14 }, { width: 20 }, { width: 16 }, { width: 18 },
    ];

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `OperationsMonitor_${new Date().toISOString().split('T')[0]}.xlsx`;
    a.click();
  };

  // ── Render ─────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* ── Stats Cards ───────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {/* Total */}
        <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
          <Card elevation={4} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{stats.total}</Typography>
                  <Typography variant="body2">{t.totalOperations}</Typography>
                </Box>
                <TotalIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
        {/* Created */}
        <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
          <Card elevation={4} sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{stats.created}</Typography>
                  <Typography variant="body2">{t.created}</Typography>
                </Box>
                <CreatedIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
        {/* Posted */}
        <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
          <Card elevation={4} sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{stats.posted}</Typography>
                  <Typography variant="body2">{t.posted}</Typography>
                </Box>
                <PostedIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
        {/* Approved */}
        <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
          <Card elevation={4} sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{stats.approved}</Typography>
                  <Typography variant="body2">{t.approved}</Typography>
                </Box>
                <ApprovedIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* ── Header & Filters ──────────────────────────────────── */}
      <Paper elevation={3} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <MonitorIcon sx={{ fontSize: 32, color: '#667eea' }} />
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#667eea' }}>
              {t.title}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title={t.refresh}>
              <IconButton
                color="primary"
                onClick={loadOperations}
                sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' } }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t.print}>
              <span>
                <IconButton
                  color="primary"
                  onClick={handlePrint}
                  disabled={filteredOperations.length === 0}
                  sx={{ bgcolor: 'rgba(102, 126, 234, 0.08)', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.18)' } }}
                >
                  <PrintIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t.exportExcel}>
              <span>
                <IconButton
                  color="success"
                  onClick={exportToExcel}
                  disabled={filteredOperations.length === 0}
                  sx={{ bgcolor: 'rgba(46, 125, 50, 0.08)', '&:hover': { bgcolor: 'rgba(46, 125, 50, 0.18)' } }}
                >
                  <ExcelIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.fromDate}
              value={filterDateFrom ? new Date(filterDateFrom) : null}
              onChange={(date) => {
                const val = toDateStr(date);
                setFilterDateFrom(val);
                if (filterDateTo && val && filterDateTo < val) setFilterDateTo('');
              }}
              format="dd/MM/yyyy"
              minDate={minDate}
              maxDate={filterDateTo ? new Date(filterDateTo) : maxDate}
              views={['year', 'month', 'day']}
              openTo="day"
              slotProps={{ textField: { size: 'small', sx: { width: 170 } } }}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.toDate}
              value={filterDateTo ? new Date(filterDateTo) : null}
              onChange={(date) => {
                const val = toDateStr(date);
                setFilterDateTo(val);
                if (filterDateFrom && val && filterDateFrom > val) setFilterDateFrom('');
              }}
              format="dd/MM/yyyy"
              minDate={filterDateFrom ? new Date(filterDateFrom) : minDate}
              maxDate={maxDate}
              views={['year', 'month', 'day']}
              openTo="day"
              slotProps={{ textField: { size: 'small', sx: { width: 170 } } }}
            />
          </LocalizationProvider>
          <TextField
            select
            label={t.documentType}
            value={filterDocType}
            onChange={(e) => setFilterDocType(e.target.value)}
            size="small"
            sx={{ width: 200 }}
          >
            {DOC_TYPES.map(dt => (
              <MenuItem key={dt} value={dt}>{getDocTypeLabel(dt)}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t.action}
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            size="small"
            sx={{ width: 150 }}
          >
            {ACTIONS.map(a => (
              <MenuItem key={a} value={a}>{getActionLabel(a)}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t.status}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            size="small"
            sx={{ width: 150 }}
          >
            {STATUSES.map(s => (
              <MenuItem key={s} value={s}>{getStatusLabel(s)}</MenuItem>
            ))}
          </TextField>
          <TextField
            label={t.search}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ width: 250 }}
            placeholder={t.searchPlaceholder}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<ClearIcon />}
            onClick={clearFilters}
            sx={{ height: 40 }}
          >
            {t.clearFilters}
          </Button>
        </Box>
      </Paper>

      {/* ── Table ─────────────────────────────────────────────── */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={48} />
          <Typography sx={{ ml: 2, mt: 1.5 }}>{t.loading}</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.documentType}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.documentNumber}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.action}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.date}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.user}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.status}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.branch}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">{t.amount}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.journalEntry}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">{t.noData}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((op, idx) => (
                  <TableRow
                    key={idx}
                    hover
                    sx={{
                      bgcolor: idx % 2 === 0
                        ? (isDark ? 'rgba(102,126,234,0.04)' : 'rgba(102,126,234,0.02)')
                        : 'inherit',
                    }}
                  >
                    <TableCell>
                      <Chip
                        label={getDocTypeLabel(op.documentType)}
                        size="small"
                        sx={{
                          bgcolor: docTypeColors[op.documentType] || '#888',
                          color: '#fff',
                          fontWeight: 'bold',
                          fontSize: '0.72rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}
                      >
                        {op.documentNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getActionLabel(op.action)}
                        size="small"
                        color={actionChipColor[op.action] || 'default'}
                        variant={op.action === 'Posted' ? 'filled' : 'outlined'}
                        sx={op.action === 'Posted' ? { bgcolor: '#1b5e20', color: '#fff' } : {}}
                      />
                    </TableCell>
                    <TableCell>{formatDate(op.actionDate)}</TableCell>
                    <TableCell>{op.actionByUserName || ''}</TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(op.status)}
                        size="small"
                        color={statusChipColor[op.status] || 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {isRTL ? (op.branchNameAr || op.branchName || '') : (op.branchName || '')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                      {formatAmount(op.amount)}
                    </TableCell>
                    <TableCell>
                      {op.journalEntryNumber || '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredOperations.length}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage={t.rowsPerPage}
          />
        </TableContainer>
      )}
    </Box>
  );
};

export default OperationsMonitorPage;
